import React from 'react';
import styled, { createGlobalStyle } from 'styled-components';

// --- Global Styles ---
const GlobalStyle = createGlobalStyle`
  body {
    margin: 0;
    padding: 0;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: #2d3748;
    line-height: 1.7;
    overflow-x: hidden;
  }
  * {
    box-sizing: border-box;
  }
  
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  @keyframes float {
    0%, 100% {
      transform: translateY(0px);
    }
    50% {
      transform: translateY(-10px);
    }
  }
`;

// --- Page Container ---
const PageContainer = styled.div`
  max-width: 100%;
  margin: 0 auto;
  background: white;
  box-shadow: 0 0 50px rgba(0, 0, 0, 0.1);
  border-radius: 0 0 20px 20px;
  overflow: hidden;
`;

// --- Top Section (Gradient Header) ---
const TopSection = styled.section`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 80px 5% 60px;
  text-align: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1000 100" fill="rgba(255,255,255,0.1)"><polygon points="1000,100 1000,0 0,100"></polygon></svg>');
    background-size: cover;
  }

  @media (max-width: 768px) {
    padding: 60px 5% 40px;
  }
`;

const TopHeadline = styled.h2`
  font-size: 3.5rem;
  font-weight: 800;
  margin: 0 0 20px;
  color: white;
  position: relative;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  animation: fadeInUp 0.8s ease-out;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }

  @media (max-width: 480px) {
    font-size: 2rem;
  }
`;

const SubHeadline = styled.p`
  font-size: 1.3rem;
  font-weight: 300;
  margin: 0;
  opacity: 0.9;
  position: relative;
  animation: fadeInUp 0.8s ease-out 0.2s both;

  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

// --- Main Content Section ---
const MainContentSection = styled.section`
  background: white;
  padding: 80px 5%;

  @media (max-width: 768px) {
    padding: 50px 5%;
  }
`;

// --- Grid for Feature Blocks ---
const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 60px;
  margin-bottom: 80px;
`;

const FeatureBlock = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  align-items: center;
  background: ${props => props.reverse ? 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)' : 'white'};
  padding: 50px;
  border-radius: 20px;
  box-shadow: ${props => props.reverse ? '0 10px 40px rgba(0, 0, 0, 0.08)' : '0 5px 25px rgba(0, 0, 0, 0.06)'};
  border: 1px solid #f1f5f9;
  transition: all 0.3s ease;
  animation: fadeInUp 0.8s ease-out;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 20px 50px rgba(0, 0, 0, 0.12);
  }

  @media (max-width: 992px) {
    grid-template-columns: 1fr;
    gap: 30px;
    padding: 40px 30px;
  }

  @media (max-width: 768px) {
    padding: 30px 20px;
  }
`;

const FeatureTextBlock = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const FeatureImageBlock = styled.div`
  position: relative;
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.1);
  animation: float 3s ease-in-out infinite;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
  }
  
  &:hover img {
    transform: scale(1.05);
  }

  @media (max-width: 992px) {
    order: -1;
    height: 300px;
  }
`;

const BlockTitle = styled.h3`
  font-size: 2.2rem;
  font-weight: 700;
  color: #1a202c;
  margin: 0 0 20px;
  background: linear-gradient(135deg, #667eea, #764ba2);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  @media (max-width: 768px) {
    font-size: 1.8rem;
  }
`;

const BlockDescription = styled.p`
  font-size: 1.1rem;
  color: #4a5568;
  margin-bottom: 30px;
  line-height: 1.8;
`;

const BenefitGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-top: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const BenefitList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const BenefitListItem = styled.li`
  font-size: 1rem;
  color: #4a5568;
  margin-bottom: 15px;
  position: relative;
  padding-left: 35px;
  line-height: 1.6;

  &:before {
    content: '✓';
    position: absolute;
    left: 0;
    top: 0;
    width: 24px;
    height: 24px;
    background: linear-gradient(135deg, #48bb78, #38a169);
    color: white;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    font-weight: bold;
  }
`;

// Image placeholders
const PlaceholderImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

// --- Content Sections ---
const ContentParagraph = styled.p`
  font-size: 1.1rem;
  color: #4a5568;
  margin-bottom: 30px;
  line-height: 1.8;
  text-align: center;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
`;

// --- Icon Row Section ---
const IconRow = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 30px;
  margin: 60px 0;
  padding: 50px 0;
  border-top: 1px solid #e2e8f0;
  border-bottom: 1px solid #e2e8f0;

  @media (max-width: 992px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 40px;
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 30px;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 25px;
  }
`;

const IconItem = styled.div`
  text-align: center;
  padding: 30px 20px;
  border-radius: 15px;
  background: linear-gradient(135deg, #f8fafc, #f1f5f9);
  transition: all 0.3s ease;
  border: 1px solid #e2e8f0;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.1);
    background: white;
  }

  &::before {
    content: '${props => props.icon}';
    display: block;
    font-size: 2.5rem;
    margin-bottom: 15px;
    background: linear-gradient(135deg, #667eea, #764ba2);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
`;

const IconText = styled.span`
  font-size: 1.1rem;
  font-weight: 600;
  color: #2d3748;
  display: block;
`;

// --- Call to Action ---
const CtaBar = styled.div`
  background: linear-gradient(135deg, #ffd89b, #19547b);
  padding: 50px 40px;
  text-align: center;
  margin: 60px 0;
  border-radius: 20px;
  position: relative;
  overflow: hidden;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.1);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1000 100" fill="rgba(255,255,255,0.1)"><polygon points="0,0 1000,100 1000,0"></polygon></svg>');
    background-size: cover;
  }
`;

const CtaText = styled.p`
  font-size: 1.5rem;
  font-weight: 600;
  color: #2d3748;
  margin: 0 0 25px;
  position: relative;
  font-style: italic;
  line-height: 1.6;

  @media (max-width: 768px) {
    font-size: 1.3rem;
  }
`;

const CtaLink = styled.a`
  background: linear-gradient(135deg, #2d3748, #4a5568);
  color: white;
  padding: 15px 35px;
  border-radius: 50px;
  text-decoration: none;
  font-size: 1.1rem;
  font-weight: 600;
  display: inline-block;
  transition: all 0.3s ease;
  position: relative;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
    background: linear-gradient(135deg, #4a5568, #2d3748);
  }
`;

// --- List Styling ---
const StyledUnorderedList = styled.ul`
  list-style: none;
  padding: 0;
  margin-bottom: 30px;
`;

const StyledListItem = styled.li`
  font-size: 1.1rem;
  color: #4a5568;
  margin-bottom: 20px;
  position: relative;
  padding-left: 40px;
  line-height: 1.7;

  &:before {
    content: '▶';
    position: absolute;
    left: 0;
    top: 2px;
    color: #667eea;
    font-size: 1.2rem;
    font-weight: bold;
  }

  strong {
    color: #2d3748;
    font-weight: 700;
  }
`;

// --- Bottom CTA ---
const BottomCtaSection = styled.div`
  background: linear-gradient(135deg, #667eea, #764ba2);
  padding: 80px 5%;
  text-align: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1000 100" fill="rgba(255,255,255,0.1)"><polygon points="0,100 1000,0 1000,100"></polygon></svg>');
    background-size: cover;
  }
`;

const BottomCtaText = styled.p`
  font-size: 1.8rem;
  font-weight: 600;
  color: white;
  margin: 0 0 40px;
  position: relative;
  line-height: 1.6;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const BottomCtaButton = styled.a`
  background: white;
  color: #667eea;
  padding: 18px 45px;
  border-radius: 50px;
  text-decoration: none;
  font-size: 1.2rem;
  font-weight: 700;
  display: inline-block;
  transition: all 0.3s ease;
  position: relative;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.2);

  &:hover {
    transform: translateY(-3px) scale(1.05);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
    background: #f7fafc;
  }
`;

// --- Main React Component ---
const Seo1 = () => {
  return (
    <>
      <GlobalStyle />
      <PageContainer>
        {/* --- Top Section --- */}
        <TopSection>
          <TopHeadline>Why Choose Us for the Best SEO Services?</TopHeadline>
          <SubHeadline>Drive More Traffic, Generate More Leads, and Dominate Your Market</SubHeadline>
        </TopSection>

        {/* --- Main Content Section --- */}
        <MainContentSection>
          <FeatureGrid>
            {/* Expertise and Innovation */}
            <FeatureBlock>
              <FeatureTextBlock>
                <BlockTitle>Expertise and Innovation</BlockTitle>
                <BlockDescription>
                  Our team of seasoned professionals stays ahead of the curve with cutting-edge SEO 
                  strategies and innovative approaches tailored to deliver optimal results for your business.
                </BlockDescription>
                <BenefitGrid>
                  <BenefitList>
                    <BenefitListItem>Data-driven insights</BenefitListItem>
                    <BenefitListItem>Proven results</BenefitListItem>
                  </BenefitList>
                  <BenefitList>
                    <BenefitListItem>Comprehensive analysis</BenefitListItem>
                    <BenefitListItem>Creative solutions</BenefitListItem>
                  </BenefitList>
                </BenefitGrid>
              </FeatureTextBlock>
              <FeatureImageBlock>
                <PlaceholderImage src="https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80" alt="SEO Expertise" />
              </FeatureImageBlock>
            </FeatureBlock>

            {/* Diverse Industry Experience */}
            <FeatureBlock reverse>
              <FeatureImageBlock>
                <PlaceholderImage src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80" alt="Diverse Industry Experience" />
              </FeatureImageBlock>
              <FeatureTextBlock>
                <BlockTitle>Diverse Industry Experience</BlockTitle>
                <BlockDescription>
                  With extensive experience across multiple industries, we develop adaptable 
                  strategies that perfectly align with your unique business needs and market dynamics.
                </BlockDescription>
                <BenefitGrid>
                  <BenefitList>
                    <BenefitListItem>Varied niches</BenefitListItem>
                    <BenefitListItem>Wide range expertise</BenefitListItem>
                  </BenefitList>
                  <BenefitList>
                    <BenefitListItem>Broad industry knowledge</BenefitListItem>
                    <BenefitListItem>Many sectors covered</BenefitListItem>
                  </BenefitList>
                </BenefitGrid>
              </FeatureTextBlock>
            </FeatureBlock>

            {/* Cutting-Edge Technology */}
            <FeatureBlock>
              <FeatureTextBlock>
                <BlockTitle>Cutting-Edge Technology</BlockTitle>
                <BlockDescription>
                  We leverage the latest technological advancements and sophisticated tools 
                  to provide in-depth analysis, continuous optimization, and superior results.
                </BlockDescription>
                <BenefitGrid>
                  <BenefitList>
                    <BenefitListItem>Innovation hub</BenefitListItem>
                    <BenefitListItem>Digital solutions</BenefitListItem>
                  </BenefitList>
                  <BenefitList>
                    <BenefitListItem>Tech-advancements</BenefitListItem>
                    <BenefitListItem>Modern tools</BenefitListItem>
                  </BenefitList>
                </BenefitGrid>
              </FeatureTextBlock>
              <FeatureImageBlock>
                <PlaceholderImage src="https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80" alt="Cutting-Edge Technology" />
              </FeatureImageBlock>
            </FeatureBlock>
          </FeatureGrid>

          {/* Regular Content Paragraphs */}
          <ContentParagraph>
            Keywords play a pivotal role in your online presence. They drive organic traffic to your
            website by helping search engines understand your content and by attracting users who
            are actively searching for information related to your business. Strategic keyword
            placement ensures your content aligns with user queries, enhancing user experience and
            boosting relevance.
          </ContentParagraph>

          {/* Icon Row */}
          <IconRow>
            <IconItem icon="🎯">
              <IconText>Relevance</IconText>
            </IconItem>
            <IconItem icon="📊">
              <IconText>Search Volume</IconText>
            </IconItem>
            <IconItem icon="⚔️">
              <IconText>Competition</IconText>
            </IconItem>
            <IconItem icon="📈">
              <IconText>Longevity</IconText>
            </IconItem>
            <IconItem icon="🎯">
              <IconText>Intent</IconText>
            </IconItem>
          </IconRow>

          {/* Call to action */}
          <CtaBar>
            <CtaText>"With custom keyword solutions, you can gain access to valuable traffic and maximize your ROI."</CtaText>
            <CtaLink href="#contact">Get Started Today!</CtaLink>
          </CtaBar>

          <ContentParagraph>
            Our comprehensive SEO services include advanced off-page optimizations that focus on 
            building your website's authority and credibility. Through strategic link building, 
            social media engagement, and brand mentions, we signal to search engines that your 
            website is trustworthy and valuable in your industry.
          </ContentParagraph>

          {/* Text with image next to it */}
          <FeatureBlock>
            <FeatureTextBlock>
              <BlockTitle>Comprehensive SEO Strategy</BlockTitle>
              <StyledUnorderedList>
                <StyledListItem>
                  <strong>Technology Integration:</strong> Advanced IT infrastructure including 
                  hardware, software, databases, and networks optimized for SEO performance.
                </StyledListItem>
                <StyledListItem>
                  <strong>Crawling and Indexing:</strong> Our experts ensure your website gets 
                  properly crawled by search engines and indexed to appear in relevant search results.
                </StyledListItem>
                <StyledListItem>
                  <strong>Security First:</strong> Implementing robust security measures to protect 
                  your website, data, and user experience from potential threats.
                </StyledListItem>
                <StyledListItem>
                  <strong>Data Protection:</strong> Comprehensive cybersecurity measures to safeguard 
                  sensitive information and maintain user trust.
                </StyledListItem>
              </StyledUnorderedList>
            </FeatureTextBlock>
            <FeatureImageBlock>
              <PlaceholderImage src="https://images.unsplash.com/photo-1563013544-824ae1b704d3?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80" alt="SEO Strategy" />
            </FeatureImageBlock>
          </FeatureBlock>
        </MainContentSection>

        {/* --- Bottom Call to Action --- */}
        <BottomCtaSection>
          <BottomCtaText>
            "Ready to expand your business and generate more qualified leads with proven SEO strategies?"
          </BottomCtaText>
          <BottomCtaButton href="#contact">
            Yes, I Want To Dominate Search Results!
          </BottomCtaButton>
        </BottomCtaSection>
      </PageContainer>
    </>
  );
};

export default Seo1;